import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ReservationSettings } from '@/hooks/useReservationSettings';

export interface TimeSlotLimit {
  id: string;
  establishment_id: string;
  time_slot_id: string | null;
  day_of_week: number;
  limit_type: 'reservations' | 'people';
  limit_value: number;
  specific_time: string | null; // HH:mm:ss or null
}

export interface EffectiveLimit {
  type: 'reservations' | 'people';
  value: number;
  source: 'specific_time' | 'specific' | 'day' | 'global';
}

interface UseTimeSlotLimitsOptions {
  establishmentId?: string;
}

/**
 * Resolves the effective limit. Hierarchy:
 * 0. Specific time (day + HH:mm exact)        → highest priority
 * 1. Specific (day + slot)
 * 2. Day fallback (day only)
 * 3. Global reservation_settings
 */
export function resolveEffectiveLimit(
  dayOfWeek: number,
  timeSlotId: string | null | undefined,
  limits: TimeSlotLimit[],
  globalSettings: Pick<ReservationSettings, 'timeLimitType' | 'maxReservationsPerTime' | 'maxPeoplePerTime'> | null,
  bookingTime?: string | null
): EffectiveLimit | null {
  const normTime = bookingTime ? bookingTime.substring(0, 5) : null;

  // 0. Specific time (heure exacte)
  if (normTime) {
    const exact = limits.find(
      l => l.day_of_week === dayOfWeek && l.specific_time && l.specific_time.substring(0, 5) === normTime
    );
    if (exact) {
      return { type: exact.limit_type, value: exact.limit_value, source: 'specific_time' };
    }
  }

  // 1. Specific: match on day + slot (without specific_time)
  if (timeSlotId) {
    const specific = limits.find(
      l => l.day_of_week === dayOfWeek && l.time_slot_id === timeSlotId && !l.specific_time
    );
    if (specific) {
      return { type: specific.limit_type, value: specific.limit_value, source: 'specific' };
    }
  }

  // 2. Day fallback
  const dayFallback = limits.find(
    l => l.day_of_week === dayOfWeek && l.time_slot_id === null && !l.specific_time
  );
  if (dayFallback) {
    return { type: dayFallback.limit_type, value: dayFallback.limit_value, source: 'day' };
  }

  // 3. Global fallback
  if (globalSettings && globalSettings.timeLimitType !== 'none') {
    if (globalSettings.timeLimitType === 'reservations' && globalSettings.maxReservationsPerTime !== null) {
      return { type: 'reservations', value: globalSettings.maxReservationsPerTime, source: 'global' };
    }
    if (globalSettings.timeLimitType === 'people' && globalSettings.maxPeoplePerTime !== null) {
      return { type: 'people', value: globalSettings.maxPeoplePerTime, source: 'global' };
    }
  }

  return null;
}

export const useTimeSlotLimits = (options: UseTimeSlotLimitsOptions = {}) => {
  const { establishmentId } = options;
  const [limits, setLimits] = useState<TimeSlotLimit[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLimits = useCallback(async () => {
    if (!establishmentId) {
      setLimits([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('time_slot_limits')
        .select('*')
        .eq('establishment_id', establishmentId);

      if (error) throw error;

      setLimits((data || []).map((row: any) => ({
        id: row.id,
        establishment_id: row.establishment_id,
        time_slot_id: row.time_slot_id,
        day_of_week: row.day_of_week,
        limit_type: row.limit_type as 'reservations' | 'people',
        limit_value: row.limit_value,
        specific_time: row.specific_time ?? null,
      })));
    } catch (error) {
      console.error('Error fetching time slot limits:', error);
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  const upsertLimit = useCallback(async (
    dayOfWeek: number,
    timeSlotId: string | null,
    limitType: 'reservations' | 'people',
    limitValue: number,
    specificTime?: string | null
  ) => {
    if (!establishmentId) return;

    // Normalize HH:mm → HH:mm:00 for Postgres TIME column
    const normalizedTime = specificTime
      ? (specificTime.length === 5 ? `${specificTime}:00` : specificTime)
      : null;

    const payload: any = {
      establishment_id: establishmentId,
      time_slot_id: normalizedTime ? null : timeSlotId, // une heure spécifique ne dépend pas du slot
      day_of_week: dayOfWeek,
      limit_type: limitType,
      limit_value: limitValue,
      specific_time: normalizedTime,
    };

    // For specific_time and day-fallback, the unique constraints are PARTIAL indexes,
    // which PostgREST's onConflict cannot target. We do a manual find+update / insert.
    if (normalizedTime) {
      const existing = limits.find(
        l => l.day_of_week === dayOfWeek
          && l.specific_time
          && l.specific_time.substring(0, 5) === normalizedTime.substring(0, 5)
      );
      if (existing) {
        const { error: updateError } = await supabase
          .from('time_slot_limits')
          .update({ limit_type: limitType, limit_value: limitValue })
          .eq('id', existing.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('time_slot_limits')
          .insert(payload);
        if (insertError) throw insertError;
      }
    } else if (!timeSlotId) {
      // Day fallback (partial unique index where time_slot_id IS NULL)
      const existing = limits.find(
        l => l.day_of_week === dayOfWeek && l.time_slot_id === null && !l.specific_time
      );
      if (existing) {
        const { error: updateError } = await supabase
          .from('time_slot_limits')
          .update({ limit_type: limitType, limit_value: limitValue })
          .eq('id', existing.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('time_slot_limits')
          .insert(payload);
        if (insertError) throw insertError;
      }
    } else {
      // Day + slot: full unique constraint exists, onConflict works
      const { error } = await supabase
        .from('time_slot_limits')
        .upsert(payload, { onConflict: 'establishment_id,time_slot_id,day_of_week' });
      if (error) throw error;
    }

    await fetchLimits();
  }, [establishmentId, fetchLimits, limits]);

  const deleteLimit = useCallback(async (limitId: string) => {
    const { error } = await supabase
      .from('time_slot_limits')
      .delete()
      .eq('id', limitId);

    if (error) throw error;
    await fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    fetchLimits();

    if (!establishmentId) return;

    const subscription = supabase
      .channel(`time_slot_limits_${establishmentId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'time_slot_limits',
        filter: `establishment_id=eq.${establishmentId}`,
      }, () => {
        fetchLimits();
      })
      .subscribe();

    return () => { subscription.unsubscribe(); };
  }, [establishmentId, fetchLimits]);

  return {
    limits,
    loading,
    upsertLimit,
    deleteLimit,
    refetch: fetchLimits,
  };
};
